﻿import { useMemo } from "react";
import { useHasPlanFeature, useHasPermission } from "@/hooks/usePermissions";
import { useQuery } from "@tanstack/react-query";
import type {
  DashboardFilters,
  TotalEmissionResponse,
  CategoryEmission,
  EmissionsSankeyItem,
} from "../types/dashboard";
import * as api from "../api/dashboardService";
import {
  getSupplierAllocatedEmissions,
  getInvestorAllocatedEmissions,
} from "@/features/suppliers/api/supplierService";
import {
  buildAllocationBreakdown,
  mergeAllocationsIntoSankey,
  mergeAllocationsIntoScopeMonth,
  mergeAllocationsIntoScopeCategory,
} from "../lib/supplierAllocations";
import { useBusinessUnitStore } from "@/stores/businessUnit";
import { STALE } from "@/lib/query-constants";
import { useProtocol } from "@/lib/protocol";
import { getCurrentUser } from "@/utils/auth";
import { toLocalIsoDate } from "@/lib/date";
import { visibleQuery } from "../lib/visibleQuery";

function shouldInjectAllocations(filters: DashboardFilters): boolean {
  return (
    (filters.scope === "all" || filters.scope === "3") &&
    filters.categoryIds.length === 0 &&
    filters.facilityIds.length === 0
  );
}

export function useSupplierAllocations(
  filters: DashboardFilters,
  applicable = true,
) {
  const hasBusinessUnits = useHasBusinessUnits();
  const companyId = getCurrentUser()?.companyId || "";
  const fromDate = toLocalIsoDate(filters.dateRange.from);
  const toDate = toLocalIsoDate(filters.dateRange.to);
  const fromMonth = fromDate.slice(0, 7),
    toMonth = toDate.slice(0, 7);
  const hasSrm = useHasPlanFeature("SRM");
  const hasSupplier = useHasPlanFeature("SUPPLIER");
  const canRead = useHasPermission(["READ_SUPPLIER_TOTAL_ALLOCATIONS"]);
  const enabled =
    applicable &&
    shouldInjectAllocations(filters) &&
    hasBusinessUnits &&
    !!companyId &&
    (hasSrm || hasSupplier) &&
    canRead;

  const defaultQuery = useQuery({
    queryKey: [
      "dashboard",
      "supplierAllocations",
      companyId,
      "supplier",
      fromDate,
      toDate,
    ],
    queryFn: () =>
      getSupplierAllocatedEmissions({ fromDate, toDate, fromMonth, toMonth }),
    enabled,
    staleTime: STALE.FIVE_MIN,
  });

  const investorQuery = useQuery({
    queryKey: [
      "dashboard",
      "supplierAllocations",
      companyId,
      "investments",
      fromDate,
      toDate,
    ],
    queryFn: () =>
      getInvestorAllocatedEmissions({ fromDate, toDate, fromMonth, toMonth }),
    enabled,
    staleTime: STALE.FIVE_MIN,
  });

  const breakdown = useMemo(
    () =>
      enabled
        ? buildAllocationBreakdown(defaultQuery.data, investorQuery.data)
        : null,
    [enabled, defaultQuery.data, investorQuery.data],
  );

  return {
    breakdown,
    isLoading: enabled && (defaultQuery.isLoading || investorQuery.isLoading),
    isFetching:
      enabled && (defaultQuery.isFetching || investorQuery.isFetching),
    isError: enabled && (defaultQuery.isError || investorQuery.isError),
    refetch: () =>
      enabled
        ? Promise.all([defaultQuery.refetch(), investorQuery.refetch()])
        : Promise.resolve([]),
  };
}

function toApiParams(filters: DashboardFilters, businessUnitIds: string) {
  return {
    businessUnitIds,
    from_date: filters.dateRange.from,
    to_date: filters.dateRange.to,
    categories:
      filters.categoryIds.length > 0 ? filters.categoryIds.join(",") : "",
    scopes: filters.scope === "all" ? undefined : filters.scope,
  };
}

function useAllBusinessUnitIds(): string {
  return useBusinessUnitStore((s) =>
    s.businessUnits.map((unit) => unit.id).join(","),
  );
}

export function useHasBusinessUnits(): boolean {
  return useBusinessUnitStore((s) => s.businessUnits.length > 0);
}

export function useSelectedOrAllBusinessUnitIds(
  filters: DashboardFilters,
): string {
  return useAuthorizedBusinessUnitIds(filters.facilityIds.join(","), true);
}

function useAuthorizedBusinessUnitIds(
  selected: string,
  allWhenEmpty = false,
): string {
  const allBusinessUnitIds = useAllBusinessUnitIds();
  const accessible = new Set(allBusinessUnitIds.split(",").filter(Boolean));
  return selected
    ? [...new Set(selected.split(","))]
        .filter((id) => accessible.has(id))
        .join(",")
    : allWhenEmpty
      ? allBusinessUnitIds
      : "";
}

function deriveTotalEmissions(
  items: EmissionsSankeyItem[],
): TotalEmissionResponse {
  const categoryMap = new Map<string, CategoryEmission>();
  let total = 0;
  for (const scopeItem of items) {
    total += scopeItem.value;
    for (const cat of scopeItem.categories) {
      const scopeCode = scopeItem.code === "Total" ? cat.code : scopeItem.code;
      const scopeNumber = scopeCode.replace(/[^0-9]/g, "") || "all";
      const subs =
        cat.subcategories && cat.subcategories.length > 0
          ? cat.subcategories
          : [{ code: cat.code, value: cat.value, name: cat.name }];
      for (const sub of subs) {
        const existing = categoryMap.get(sub.code);
        if (existing) {
          existing.emissionvalue += sub.value;
        } else {
          categoryMap.set(sub.code, {
            category: sub.name,
            code: sub.code,
            emissionvalue: sub.value,
            name: sub.name,
            scope: scopeNumber,
            scope_code: scopeCode,
          });
        }
      }
    }
  }
  const categoryData = [...categoryMap.values()].sort(
    (a, b) => b.emissionvalue - a.emissionvalue,
  );
  return {
    total_emission_value: total,
    uom: "kgCO2e",
    conversion_value: 1,
    categoryData,
  };
}

export function useTotalEmissions(filters: DashboardFilters) {
  const sankey = useEmissionsSankey(filters);
  const data = useMemo(
    () => (sankey.data ? deriveTotalEmissions(sankey.data) : undefined),
    [sankey.data],
  );
  return { ...sankey, data };
}

export function useEmissionsByScopeAndMonth(filters: DashboardFilters) {
  const protocol = useProtocol();
  const businessUnitIds = useSelectedOrAllBusinessUnitIds(filters);
  const allowed = useHasPlanFeature("EMISSIONS");
  const enabled = allowed && !!businessUnitIds;
  const query = visibleQuery(
    useQuery({
      queryKey: [
        "dashboard",
        "emissionsByScopeAndMonth",
        protocol,
        filters,
        businessUnitIds,
      ],
      queryFn: () =>
        api.getEmissionsByScopeAndMonth(toApiParams(filters, businessUnitIds)),
      enabled,
    }),
    enabled,
  );
  const {
    breakdown,
    isLoading: allocationLoading,
    isError: allocationError,
    refetch: refetchAllocations,
  } = useSupplierAllocations(filters, enabled);
  const data = useMemo(
    () =>
      query.data
        ? mergeAllocationsIntoScopeMonth(query.data, breakdown)
        : query.data,
    [query.data, breakdown],
  );
  return {
    ...query,
    data,
    isLoading: query.isLoading || allocationLoading,
    isError: query.isError || allocationError,
    refetch: async () => {
      await refetchAllocations();
      return query.refetch();
    },
  };
}

export function useEmissionsSankey(filters: DashboardFilters) {
  const protocol = useProtocol();
  const businessUnitIds = useSelectedOrAllBusinessUnitIds(filters);
  const allowed = useHasPlanFeature("EMISSIONS");
  const enabled = allowed && !!businessUnitIds;
  const query = visibleQuery(
    useQuery({
      queryKey: [
        "dashboard",
        "emissionsSankey",
        protocol,
        filters,
        businessUnitIds,
      ],
      queryFn: () => api.getAllEmissions(toApiParams(filters, businessUnitIds)),
      enabled,
    }),
    enabled,
  );
  const {
    breakdown,
    isLoading: allocationLoading,
    isError: allocationError,
    refetch: refetchAllocations,
  } = useSupplierAllocations(filters, enabled);
  const data = useMemo(
    () =>
      query.data
        ? mergeAllocationsIntoSankey(query.data, breakdown, protocol)
        : query.data,
    [query.data, breakdown, protocol],
  );
  return {
    ...query,
    data,
    isLoading: query.isLoading || allocationLoading,
    isError: query.isError || allocationError,
    refetch: async () => {
      await refetchAllocations();
      return query.refetch();
    },
  };
}

export function useEmissionsByFacility(filters: DashboardFilters) {
  const protocol = useProtocol();
  const businessUnitIds = useSelectedOrAllBusinessUnitIds(filters);
  const allowed = useHasPlanFeature("EMISSIONS");
  const enabled = allowed && !!businessUnitIds;
  return visibleQuery(
    useQuery({
      queryKey: [
        "dashboard",
        "emissionsByFacility",
        protocol,
        filters,
        businessUnitIds,
      ],
      queryFn: () =>
        api.getEmissionsByUnit(toApiParams(filters, businessUnitIds)),
      enabled,
    }),
    enabled,
  );
}

export function useEmissionsByActivity(filters: DashboardFilters) {
  const protocol = useProtocol();
  const businessUnitIds = useSelectedOrAllBusinessUnitIds(filters);
  const allowed = useHasPlanFeature("EMISSIONS");
  const enabled = allowed && !!businessUnitIds;
  return visibleQuery(
    useQuery({
      queryKey: [
        "dashboard",
        "emissionsByActivity",
        protocol,
        filters,
        businessUnitIds,
      ],
      queryFn: () =>
        api.getEmissionsByActivity(toApiParams(filters, businessUnitIds)),
      enabled,
    }),
    enabled,
  );
}

export function useEmissionsByScopeAndCategory(filters: DashboardFilters) {
  const protocol = useProtocol();
  const businessUnitIds = useSelectedOrAllBusinessUnitIds(filters);
  const allowed = useHasPlanFeature("EMISSIONS");
  const enabled = allowed && !!businessUnitIds;
  const query = visibleQuery(
    useQuery({
      queryKey: [
        "dashboard",
        "emissionsByScopeAndCategory",
        protocol,
        filters,
        businessUnitIds,
      ],
      queryFn: () =>
        api.getEmissionsByScopeAndCategories(
          toApiParams(filters, businessUnitIds),
        ),
      enabled,
    }),
    enabled,
  );
  const {
    breakdown,
    isLoading: allocationLoading,
    isError: allocationError,
    refetch: refetchAllocations,
  } = useSupplierAllocations(filters, enabled);
  const data = useMemo(
    () =>
      query.data
        ? mergeAllocationsIntoScopeCategory(query.data, breakdown, protocol)
        : query.data,
    [query.data, breakdown, protocol],
  );
  return {
    ...query,
    data,
    isLoading: query.isLoading || allocationLoading,
    isError: query.isError || allocationError,
    refetch: async () => {
      await refetchAllocations();
      return query.refetch();
    },
  };
}

export function useEmissionsByYear(
  selectedBusinessUnit: string,
  scope: string,
  category: string,
) {
  const protocol = useProtocol();
  const businessUnit = useAuthorizedBusinessUnitIds(selectedBusinessUnit);
  const allowed = useHasPlanFeature("EMISSIONS");
  const enabled = allowed && !!businessUnit;
  return visibleQuery(
    useQuery({
      queryKey: [
        "dashboard",
        "emissionsByYear",
        protocol,
        businessUnit,
        scope,
        category,
      ],
      queryFn: () => api.getEmissionsByYear(businessUnit, scope, category),
      enabled,
    }),
    enabled,
  );
}

export function useTargetActual(selectedBusinessUnit: string, years: string) {
  const protocol = useProtocol();
  const allowed = useHasPlanFeature("TARGETS");
  const businessUnit = useAuthorizedBusinessUnitIds(selectedBusinessUnit);
  const enabled = allowed && !!businessUnit && !!years;
  const query = useQuery({
    queryKey: ["dashboard", "targetActual", protocol, businessUnit, years],
    queryFn: () => api.getTargetActualEmissions(businessUnit, years),
    enabled,
  });
  return visibleQuery(query, enabled);
}

export function useTargetCategories(
  selectedBusinessUnit: string,
  applicable = true,
) {
  const allowed = useHasPlanFeature("TARGETS");
  const businessUnit = useAuthorizedBusinessUnitIds(selectedBusinessUnit);
  const enabled = allowed && applicable && !!businessUnit;
  return visibleQuery(
    useQuery({
      queryKey: ["dashboard", "targetCategories", businessUnit],
      queryFn: () => api.getTargetCategories(businessUnit),
      enabled,
    }),
    enabled,
  );
}

export function useTargetsByYear(
  selectedBusinessUnit: string,
  type: string,
  scope: string,
  category: string,
) {
  const protocol = useProtocol();
  const allowed = useHasPlanFeature("TARGETS");
  const businessUnit = useAuthorizedBusinessUnitIds(selectedBusinessUnit);
  const enabled =
    allowed &&
    !!businessUnit &&
    (type !== "Category" || !!category) &&
    (type !== "Scope" || !!scope);
  const query = useQuery({
    queryKey: [
      "dashboard",
      "targetsByYear",
      protocol,
      businessUnit,
      type,
      scope,
      category,
    ],
    queryFn: () => api.getTargetsByYear(businessUnit, type, scope, category),
    enabled,
  });
  return visibleQuery(query, enabled);
}

export function useMetricsByYear(
  selectedBusinessUnit: string,
  metric: string,
  currentTabName: string,
  scope: string,
  category: string,
) {
  const protocol = useProtocol();
  const allowed = useHasPlanFeature("TARGETS");
  const businessUnit = useAuthorizedBusinessUnitIds(selectedBusinessUnit);
  const enabled =
    allowed &&
    !!businessUnit &&
    !!metric &&
    (currentTabName !== "Category" || !!category) &&
    (currentTabName !== "Scope" || !!scope);
  const query = useQuery({
    queryKey: [
      "dashboard",
      "metricsByYear",
      protocol,
      businessUnit,
      metric,
      currentTabName,
      scope,
      category,
    ],
    queryFn: () =>
      api.getMetricsByYear(
        businessUnit,
        metric,
        currentTabName,
        scope,
        category,
      ),
    enabled,
  });
  return visibleQuery(query, enabled);
}

export function useOffsetsByMonth(
  businessUnits: string,
  fromMonth: Date,
  toMonth: Date,
) {
  const allowed = useHasPlanFeature("OFFSETS");
  const selectedBusinessUnits = useAuthorizedBusinessUnitIds(
    businessUnits,
    true,
  );
  const enabled = allowed && !!selectedBusinessUnits;

  const query = useQuery({
    queryKey: [
      "dashboard",
      "offsetsByMonth",
      selectedBusinessUnits,
      fromMonth,
      toMonth,
    ],
    queryFn: () =>
      api.getOffsetsByMonth({
        businessUnits: selectedBusinessUnits,
        fromMonth,
        toMonth,
      }),
    enabled,
  });
  return visibleQuery(query, enabled);
}

export function useFacilities() {
  return useQuery({
    queryKey: ["dashboard", "facilities"],
    queryFn: () => api.getFacilities(),
    staleTime: STALE.THIRTY_MIN,
  });
}

export function useCategories(company: string) {
  const protocol = useProtocol();
  const allowed = useHasPlanFeature("EMISSIONS");
  const enabled = allowed && !!company;
  return visibleQuery(
    useQuery({
      queryKey: ["dashboard", "categories", company, protocol],
      queryFn: () => api.getAvailableCategories(company),
      staleTime: STALE.THIRTY_MIN,
      enabled,
    }),
    enabled,
  );
}

export function useOffsetsByYear(selectedBusinessUnits: string) {
  const allowed = useHasPlanFeature("OFFSETS");
  const businessUnits = useAuthorizedBusinessUnitIds(selectedBusinessUnits);
  const enabled = allowed && !!businessUnits;
  const query = useQuery({
    queryKey: ["dashboard", "offsetsByYear", businessUnits],
    queryFn: () => api.getOffsetsByYear(businessUnits),
    enabled,
  });
  return visibleQuery(query, enabled);
}

export function useCustomMetrics(selectedBusinessUnit: string) {
  const allowed = useHasPlanFeature("TARGETS");
  const businessUnit = useAuthorizedBusinessUnitIds(selectedBusinessUnit);
  const enabled = allowed && !!businessUnit;
  const query = useQuery({
    queryKey: ["dashboard", "customMetrics", businessUnit],
    queryFn: () => api.getCustomMetrics(businessUnit),
    staleTime: STALE.THIRTY_MIN,
    enabled,
  });
  return visibleQuery(query, enabled);
}
